// Old fork only: the redesign uses SearchModeDropdown instead.
// ResultsDropdown/StyledComponents.tsx restyles it with `styled()`.
import React from 'react';
import styled from 'styled-components';
import { Switch, textStyles, token, TruncatedText } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { useNavigableItem } from '../KeyboardNavigation';
import { useSearchMode, useSpotlightDropdownContext } from '../SpotlightState';

export const BANNER_NLS_HEIGHT = 72;

interface Props {
	className?: string;
}

// FIX: TODO: This uses some copy-pasting from the Design System's ListItem component. Once it allows for custom background colors, we can use it instead of reimplementing it
export function BannerNLS({ className }: Props) {
	const { refInput } = useSpotlightDropdownContext();
	const { isNLSSearchOn, isHybridSearchEnabled, setIsNLSSearchOn } = useSearchMode();
	const { translate } = useTranslate();

	const hybridAwareSubtitleKey = isHybridSearchEnabled
		? 'spotlight.nlsBannerSubtitleHybrid'
		: 'spotlight.nlsBannerSubtitle';

	const toggleNLSAndFocusSpotlight = (event?: React.MouseEvent) => {
		event?.stopPropagation(); // Otherwise, it counts as a "click outside the spotlight input" and it closes the dropdown
		setIsNLSSearchOn((prev) => !prev);
		refInput.current?.focus();
	};

	const { isActive, itemRef } = useNavigableItem<HTMLDivElement>(
		() => toggleNLSAndFocusSpotlight(),
		{ afterSelect: 'none' },
	);

	return (
		<ItemStyled
			ref={itemRef}
			$isActive={isActive}
			$isNLSOn={isNLSSearchOn}
			onClick={toggleNLSAndFocusSpotlight}
			className={className}
		>
			<Content id="labelId">
				<TruncatedText>
					{isNLSSearchOn
						? translate('spotlight.nlsBannerTitle')
						: translate('spotlight.nlsBannerTitleOff')}
				</TruncatedText>
				<Subtext>
					<TruncatedText>
						{isNLSSearchOn
							? translate(hybridAwareSubtitleKey)
							: translate('spotlight.nlsBannerSubtitleOff')}
					</TruncatedText>
				</Subtext>
			</Content>

			<RightElements
				onClick={(event) => {
					// If not prevent propagation, then onClick would be triggered multiple times on Item, which results in incorrect state
					event.stopPropagation();
				}}
				onKeyDown={(event) => {
					// If not prevent propagation, then onClick would be triggered multiple times on Item, which results in incorrect state
					event.stopPropagation();
				}}
			>
				<CustomSwitch
					isChecked={isNLSSearchOn}
					aria-labelledby="labelId"
					onChange={() => toggleNLSAndFocusSpotlight()}
					data-heapId="discover_spotlight-nls-switch"
				/>
			</RightElements>
		</ItemStyled>
	);
}

const ItemStyled = styled.div<{ $isActive: boolean; $isNLSOn: boolean }>`
	display: flex;

	margin: ${token.spacing3} ${token.spacing5};
	padding: ${token.spacing4} ${token.spacing5};
	background-color: ${({ $isActive, $isNLSOn }) => {
		if ($isActive && $isNLSOn) {
			return token.purple100;
		}

		if ($isActive && !$isNLSOn) {
			return token.gray25;
		}

		if ($isNLSOn) {
			return '#f3f2fd';
		}

		return '#f2f3f4';
	}};
	border-radius: 16px;
	max-height: ${BANNER_NLS_HEIGHT}px;

	&:hover {
		cursor: pointer;
		background-color: ${({ $isNLSOn }) => ($isNLSOn ? '#f2f3f4' : '#f3f2fd')};
	}
`;

const Content = styled.div`
	${textStyles.uiTextM}

	flex: 1;
	min-width: 0;
	font-weight: 400;
	color: #031026;
`;

const Subtext = styled.div`
	${textStyles.uiTextS}

	color: ${token.colorTextMuted};
`;

const RightElements = styled.div`
	display: flex;
	flex: none;
	gap: ${token.spacing3};
	align-items: center;
	margin-left: ${token.spacing4};
`;

const CustomSwitch = styled(Switch)`
	input[role='switch']:checked + span[aria-hidden='true'] {
		background-color: #7064e4;
	}

	input[role='switch']:checked:hover + span[aria-hidden='true'],
	input[role='switch']:checked:focus-visible + span[aria-hidden='true'] {
		background-color: #534aa9;
	}

	input[role='switch']:not(:checked):hover + span[aria-hidden='true'],
	input[role='switch']:not(:checked):focus-visible + span[aria-hidden='true'] {
		background-color: rgba(3, 16, 38, 0.28);
	}

	input[role='switch']:focus-visible + span[aria-hidden='true'] {
		outline: 2px solid #7064e4;
		outline-offset: 2px;
	}
`;
